import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as authService from '../utils/authService';
import * as stripeService from '../utils/stripeService';

// Create the context
export const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Sign up
  const signUp = async (email, password, fullName) => {
    try {
      setError(null);
      const result = await authService.signUp(email, password, fullName);
      
      // Store email in localStorage for future use
      if (email) {
        localStorage.setItem('userEmail', email);
      }
      
      return result;
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message || 'Failed to sign up');
      throw err;
    }
  };

  // Sign in
  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await authService.signIn(email, password);
      
      // Store email in localStorage for future use
      if (email) {
        localStorage.setItem('userEmail', email);
      }
      
      return result;
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in');
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      setIsSigningOut(true);
      
      // Clear subscription data for this user
      if (user?.id) {
        stripeService.clearSubscriptionStatus(user.id);
      }
      
      await authService.signOut();
      
      // Clear state
      setUser(null);
      setProfile(null);
      setSubscription(null);
      
      return true;
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err.message || 'Failed to sign out');
      throw err;
    } finally {
      setIsSigningOut(false);
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      const updatedProfile = await authService.updateUserProfile(user.id, profileData);
      setProfile(updatedProfile);
      return updatedProfile;
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err.message || 'Failed to update profile');
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError(null);
      return await authService.resetPassword(email);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to reset password');
      throw err;
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      setError(null);
      return await authService.updatePassword(newPassword);
    } catch (err) {
      console.error('Update password error:', err);
      setError(err.message || 'Failed to update password');
      throw err;
    }
  };

  // Clear any error
  const clearError = () => {
    setError(null);
  };

  // Load user profile - memoized to prevent unnecessary rerenders
  const loadUserProfile = useCallback(async (userId) => {
    try {
      const userProfile = await authService.getUserProfile(userId);
      setProfile(userProfile);
    } catch (profileError) {
      console.error('Error loading user profile:', profileError);
      // Continue even if profile loading fails
    }
  }, []);

  // Load subscription data for a user
  const loadSubscription = useCallback(async (userId) => {
    if (!userId) return null;
    
    try {
      setSubscriptionLoading(true);
      console.log('Loading subscription for user:', userId);
      
      // Always try to get subscription from API first
      try {
        const subscriptionData = await stripeService.getSubscriptionStatus(userId);
        console.log('Loaded subscription data from API:', subscriptionData);
        
        // If subscription is active, update local storage
        if (subscriptionData?.active) {
          stripeService.saveSubscriptionStatus(userId, true);
          setSubscription(subscriptionData);
          return subscriptionData;
        } else {
          // If API says not active, clear local storage and set subscription to inactive
          stripeService.clearSubscriptionStatus(userId);
          setSubscription({ active: false });
          return { active: false };
        }
      } catch (err) {
        console.error('Error loading subscription from API:', err);
        
        // If API fails, fall back to local storage
        const localStatus = stripeService.getLocalSubscriptionStatus(userId);
        console.log('Falling back to local storage status:', localStatus);
        
        // Use local status as fallback
        setSubscription({ active: localStatus });
        return { active: localStatus };
      }
    } catch (err) {
      console.error('Error in loadSubscription:', err);
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  // Mark user as having an active subscription
  const markUserSubscriptionActive = (userId) => {
    if (!userId) return;
    
    try {
      stripeService.saveSubscriptionStatus(userId, true);
      setSubscription({ active: true });
    } catch (err) {
      console.error('Error marking user subscription active:', err);
    }
  };

  // Check if user has an active subscription
  const hasActiveSubscription = useCallback(() => {
    // First check if subscription from API is active
    if (subscription?.active) {
      return true;
    }
    
    // Then check local storage for user-specific subscription
    if (user?.id && stripeService.getLocalSubscriptionStatus(user.id)) {
      return true;
    }
    
    return false;
  }, [subscription, user]);

  // Load user on mount
  useEffect(() => {
    let isMounted = true;
    
    const loadUser = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        
        if (!isMounted) return;
        
        setUser(currentUser);

        if (currentUser) {
          loadUserProfile(currentUser.id);
          loadSubscription(currentUser.id);
        }
      } catch (err) {
        console.error('Error loading user:', err);
        // Don't set error on initial load, only log it
      } finally {
        // Always set loading to false, even if there's an error
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Set a timeout to ensure loading state doesn't get stuck
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 3000); // 3 second timeout (reduced from 5 seconds)

    loadUser();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [loadUserProfile, loadSubscription]);

  // Set up auth state listener
  useEffect(() => {
    let isMounted = true;
    
    const handleAuthChange = async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (!isMounted) return;
      
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        loadUserProfile(session.user.id);
        loadSubscription(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        setSubscription(null);
      } else if (event === 'USER_UPDATED') {
        setUser(session?.user || null);
        if (session?.user) {
          loadUserProfile(session.user.id);
          loadSubscription(session.user.id);
        }
      }
    };
    
    const unsubscribe = authService.onAuthStateChange(handleAuthChange);

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [loadUserProfile, loadSubscription]);

  // The context value
  const value = {
    user,
    profile,
    subscription,
    loading,
    subscriptionLoading,
    error,
    isAuthenticated: !!user,
    hasActiveSubscription: hasActiveSubscription(),
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    clearError,
    isSigningOut,
    loadSubscription,
    markUserSubscriptionActive
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
